import React, { useEffect, useState } from 'react';

import { FormikValues, useFormik } from 'formik';
import { DateTime } from 'luxon';

import {
  adminActions,
  getPendingRefundFilter,
  getPendingRefunds,
  isLoadingSelector,
  pendingRefundList,
  PendingRequestTabTypes,
} from '@pharmaplan/common';

import usePendingRefundActions from '../../../hooks/Admin/PendingRefunds/usePendingRefundActions';
import usePendingRefundTabs from '../../../hooks/Admin/PendingRefunds/usePendingRefundsTabs';
import { MomentTimeFormats, ScreenTypes } from '../../../helpers/Constants';
import useAdminPermissions from '../../../hooks/Admin/useAdminPermissions';
import useUserProfileLink from '../../../hooks/Admin/useUserProfileLink';
import { renderScreen, setOpen } from '../../../actions/drawerActions';
import useSelectedTabs from '../../../hooks/Admin/useSelectedTabs';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { IDynamicTableObject } from '../../DynamicTable/types';
import useTableSort from '../../../hooks/useTableSort';
import DynamicTable from '../../DynamicTable';

import { cancellationRefundHeader, pendingRefundRowConfig } from './helper';
import PendingRequestsHeader from './PendingRequestsHeader';

const { getPendingRefunds: getPendingAction } = adminActions;

const loadingActions = [getPendingAction];

const PendingRequests = () => {
  const dispatch = useAppDispatch();

  const { can } = useAdminPermissions();
  const { handleSorting, resetSorting, orderBy, isDesc, order } = useTableSort();
  const { goToUserProfile } = useUserProfileLink();
  const { approveAction, rejectRefundForm } = usePendingRefundActions();

  const [page, setPage] = useState(1);
  const { selectedTabs, setSelectedTabs } = useSelectedTabs(
    PendingRequestTabTypes.pendingRefunds,
  );

  const { config } = usePendingRefundTabs(selectedTabs);
  const { api } = config ?? {};

  const isLoading = useAppSelector((state) =>
    isLoadingSelector(loadingActions, state));

  const list = useAppSelector(pendingRefundList);
  const { data } = list ?? {};

  useEffect(() => {
    dispatch(api({ page: 1 }));
    dispatch(getPendingRefundFilter());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToLink = (refundRequestId: string) =>
    () => {
      dispatch(setOpen(true));
      dispatch(
        renderScreen({
          screenType: ScreenTypes.pendingRefunds,
          screenNumber: 1,
          eventId: refundRequestId,
        }),
      );
    };

  const table: IDynamicTableObject = {
    headers: cancellationRefundHeader(can),
    rows: pendingRefundRowConfig(
      data,
      can,
      goToLink,
      goToUserProfile,
      approveAction,
      rejectRefundForm,
    ),
    headerBar: [],
  };

  const handleSubmit = (values: FormikValues) => {
    const { startDate, endDate, pharmacyId, status } = values ?? {};
    setPage(1);

    dispatch(
      getPendingRefunds({
        page: 1,
        status,
        startDate: startDate
          ? (startDate as DateTime).toFormat(MomentTimeFormats.fullDate)
          : null,
        endDate: endDate
          ? (endDate as DateTime).toFormat(MomentTimeFormats.fullDate)
          : null,
        pharmacyId,
        sortBy: orderBy,
        desc: isDesc,
      }),
    );
  };

  const formik = useFormik({
    initialValues: {
      startDate: null,
      endDate: null,
      pharmacyId: '',
      pharmacies: '',
      pharmacySelected: '',
      status: '',
    },
    onSubmit: handleSubmit,
  });

  const reset = () => {
    setPage(1);
    resetSorting();
    formik.resetForm();
    dispatch(getPendingRefunds({ page: 1 }));
  };

  const handlePagination = (_: unknown, selectedPage: number) => {
    const { startDate, endDate, pharmacyId, status } = formik.values ?? {};
    setPage(selectedPage + 1);
    dispatch(api({ page }));

    dispatch(
      getPendingRefunds({
        page: selectedPage,
        status,
        startDate: startDate
          ? (startDate as DateTime).toFormat(MomentTimeFormats.fullDate)
          : null,
        endDate: endDate
          ? (endDate as DateTime).toFormat(MomentTimeFormats.fullDate)
          : null,
        pharmacyId,
        sortBy: orderBy,
        desc: isDesc,
      }),
    );
  };

  useEffect(() => {
    if (orderBy) {
      setPage(1);
      handleSubmit(formik.values);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderBy, order]);

  return (
    <DynamicTable
      customHeader={(
        <PendingRequestsHeader
          selectedTab={selectedTabs}
          setSelected={setSelectedTabs}
          formik={formik}
          handleReset={reset}
        />
      )}
      showHeader={false}
      handlePagination={handlePagination}
      handleSort={handleSorting}
      loadSuccess={!isLoading}
      order={order}
      orderBy={orderBy}
      table={table}
    />
  );
};

export default PendingRequests;
